import { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react'
import * as DialogPrimitive from '@radix-ui/react-dialog'
import { AnimatePresence, motion, useReducedMotion } from 'motion/react'
import { ChevronDown, ChevronUp, Download, ExternalLink, FileText, Loader2, Maximize2, Minus, Plus, X } from 'lucide-react'
import { cn } from '@/lib/utils'
import type { PdfDocument } from './pdf-engine'

/**
 * The in-app PDF viewer.
 *
 * Pages are drawn by PDF.js onto our own canvases, so the whole experience is ours: an
 * immersive dark stage, pages as crisp white sheets, a floating glass control bar that steps
 * aside while you read and returns when you move, smooth zoom (buttons, ⌘/Ctrl + scroll,
 * trackpad pinch) anchored where you point, a live page counter, and keyboard navigation.
 * Pages render only as they approach the screen, at device resolution.
 *
 * Bytes come from the API (`getBytes`) because the file buckets do not allow cross-origin
 * reads. A file too large for that, or a browser where PDF.js fails, falls back to the
 * browser's own viewer inside the same stage, so a PDF always opens.
 */

export const isPdf = (file: { contentType: string; name: string }) =>
  file.contentType === 'application/pdf' || /\.pdf$/i.test(file.name)

/** Kept for callers that decide between the viewer and a tab; PDF.js renders everywhere. */
export function canViewPdfInApp(): boolean {
  return true
}

export type PdfBytes = { tooLarge: true; size?: number } | { tooLarge: false; base64: string }
type GetUrl = (view: 'inline' | 'download') => Promise<string>

const SPRING = { type: 'spring', stiffness: 380, damping: 34, mass: 0.9 } as const
const ZOOM_MIN = 0.5
const ZOOM_MAX = 4
const PAGE_GAP = 20

export function PdfViewer({
  open,
  onOpenChange,
  name,
  sizeLabel,
  getUrl,
  getBytes,
}: {
  open: boolean
  onOpenChange: (open: boolean) => void
  name: string
  sizeLabel?: string
  getUrl: GetUrl
  getBytes?: () => Promise<PdfBytes>
}) {
  return (
    <DialogPrimitive.Root open={open} onOpenChange={onOpenChange}>
      <AnimatePresence>
        {open ? <Stage key="pdf" name={name} sizeLabel={sizeLabel} getUrl={getUrl} getBytes={getBytes} /> : null}
      </AnimatePresence>
    </DialogPrimitive.Root>
  )
}

function base64ToBytes(b64: string): Uint8Array {
  const bin = atob(b64)
  const out = new Uint8Array(bin.length)
  for (let i = 0; i < bin.length; i += 1) out[i] = bin.charCodeAt(i)
  return out
}

type Load =
  | { state: 'loading' }
  | { state: 'ready'; doc: PdfDocument; sizes: Array<{ w: number; h: number }> }
  | { state: 'native'; url: string }
  | { state: 'error' }

function Stage({ name, sizeLabel, getUrl, getBytes }: { name: string; sizeLabel?: string; getUrl: GetUrl; getBytes?: () => Promise<PdfBytes> }) {
  const reduce = useReducedMotion()
  const [load, setLoad] = useState<Load>({ state: 'loading' })
  const [zoom, setZoom] = useState(1)
  const [page, setPage] = useState(1)
  const [chrome, setChrome] = useState(true)
  const [downloading, setDownloading] = useState(false)
  const [width, setWidth] = useState(0)
  const scrollRef = useRef<HTMLDivElement>(null)
  const zoomRef = useRef(zoom)
  const anchorRef = useRef<{ fx: number; fy: number; px: number; py: number } | null>(null)
  const hideTimer = useRef<number | undefined>(undefined)

  // Load: our renderer when the API can hand us the bytes; the browser's viewer otherwise.
  useEffect(() => {
    let live = true
    let doc: PdfDocument | null = null
    const native = async () => {
      const url = await getUrl('inline')
      if (live) setLoad({ state: 'native', url })
    }
    ;(async () => {
      try {
        const bytes = getBytes ? await getBytes() : ({ tooLarge: true } as const)
        if (bytes.tooLarge) return native()
        const { openPdf } = await import('./pdf-engine')
        doc = await openPdf(base64ToBytes(bytes.base64))
        const sizes = await Promise.all(
          Array.from({ length: doc.numPages }, async (_, i) => {
            const p = await doc!.getPage(i + 1)
            const v = p.getViewport({ scale: 1 })
            return { w: v.width, h: v.height }
          }),
        )
        if (live) setLoad({ state: 'ready', doc, sizes })
      } catch {
        try {
          await native()
        } catch {
          if (live) setLoad({ state: 'error' })
        }
      }
    })()
    return () => {
      live = false
      void doc?.destroy()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- once per open
  }, [])

  // Fit-to-width baseline, from the stage's width.
  useLayoutEffect(() => {
    const el = scrollRef.current
    if (!el || typeof ResizeObserver === 'undefined') return
    const ro = new ResizeObserver(([e]) => setWidth(e!.contentRect.width))
    ro.observe(el)
    return () => ro.disconnect()
  }, [load.state])

  const sizes = load.state === 'ready' ? load.sizes : []
  const widest = Math.max(1, ...sizes.map((s) => s.w))
  const fit = width ? Math.min(1.6, (Math.min(width, 980) - 32) / widest) : 1
  const scale = fit * zoom

  const applyZoom = useCallback((next: number, px?: number, py?: number) => {
    const el = scrollRef.current
    const z = Math.min(ZOOM_MAX, Math.max(ZOOM_MIN, Math.round(next * 100) / 100))
    if (!el || z === zoomRef.current) return
    const r = el.getBoundingClientRect()
    const x = (px ?? r.left + r.width / 2) - r.left
    const y = (py ?? r.top + r.height / 2) - r.top
    anchorRef.current = { fx: (el.scrollLeft + x) / el.scrollWidth, fy: (el.scrollTop + y) / el.scrollHeight, px: x, py: y }
    zoomRef.current = z
    setZoom(z)
  }, [])

  // Keep the point under the cursor where it was after a zoom.
  useLayoutEffect(() => {
    const a = anchorRef.current
    const el = scrollRef.current
    if (!a || !el) return
    el.scrollLeft = a.fx * el.scrollWidth - a.px
    el.scrollTop = a.fy * el.scrollHeight - a.py
    anchorRef.current = null
  }, [zoom])

  // Pinch (ctrl + wheel on trackpads) and ⌘/Ctrl + scroll.
  useEffect(() => {
    const el = scrollRef.current
    if (!el) return
    const onWheel = (e: WheelEvent) => {
      if (!e.ctrlKey && !e.metaKey) return
      e.preventDefault()
      applyZoom(zoomRef.current * Math.exp(-e.deltaY * 0.0035), e.clientX, e.clientY)
    }
    el.addEventListener('wheel', onWheel, { passive: false })
    return () => el.removeEventListener('wheel', onWheel)
  }, [applyZoom, load.state])

  // The current page: whichever page's band holds the stage's upper third.
  const onScroll = () => {
    const el = scrollRef.current
    if (!el || !sizes.length) return
    const probe = el.scrollTop + el.clientHeight / 3
    let top = PAGE_GAP
    for (let i = 0; i < sizes.length; i += 1) {
      const h = sizes[i]!.h * scale
      if (probe < top + h + PAGE_GAP / 2) {
        setPage(i + 1)
        break
      }
      top += h + PAGE_GAP
    }
    wake()
  }

  const goTo = (n: number) => {
    const el = scrollRef.current
    if (!el || !sizes.length) return
    const target = Math.min(sizes.length, Math.max(1, n))
    let top = PAGE_GAP
    for (let i = 0; i < target - 1; i += 1) top += sizes[i]!.h * scale + PAGE_GAP
    el.scrollTo({ top: top - 12, behavior: reduce ? 'auto' : 'smooth' })
  }

  // Controls step aside while reading and come back on any movement.
  const wake = useCallback(() => {
    setChrome(true)
    window.clearTimeout(hideTimer.current)
    hideTimer.current = window.setTimeout(() => setChrome(false), 2600)
  }, [])
  useEffect(() => {
    wake()
    return () => window.clearTimeout(hideTimer.current)
  }, [wake])

  const onKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === '+' || e.key === '=') applyZoom(zoomRef.current * 1.2)
    else if (e.key === '-') applyZoom(zoomRef.current / 1.2)
    else if (e.key === '0') applyZoom(1)
    else if (e.key === 'ArrowRight' || e.key === 'PageDown') goTo(page + 1)
    else if (e.key === 'ArrowLeft' || e.key === 'PageUp') goTo(page - 1)
    else if (e.key === 'Home') goTo(1)
    else if (e.key === 'End') goTo(sizes.length)
    else return
    e.preventDefault()
    wake()
  }

  const download = async () => {
    setDownloading(true)
    try {
      const href = await getUrl('download')
      const a = document.createElement('a')
      a.href = href
      a.download = name
      a.rel = 'noreferrer'
      document.body.appendChild(a)
      a.click()
      a.remove()
    } finally {
      setDownloading(false)
    }
  }
  const openTab = async () => {
    const tab = window.open('', '_blank', 'noopener,noreferrer')
    try {
      const url = load.state === 'native' ? load.url : await getUrl('inline')
      if (tab) tab.location.assign(url)
    } catch {
      tab?.close()
    }
  }

  const fade = reduce ? { initial: false, animate: { opacity: 1 }, exit: { opacity: 0 } } : { initial: { opacity: 0 }, animate: { opacity: 1 }, exit: { opacity: 0 } }

  return (
    <DialogPrimitive.Portal forceMount>
      <DialogPrimitive.Overlay forceMount asChild>
        <motion.div {...fade} transition={{ duration: 0.22 }} className="fixed inset-0 z-modal bg-black/80 backdrop-blur-xl" />
      </DialogPrimitive.Overlay>
      <DialogPrimitive.Content
        forceMount
        asChild
        aria-describedby={undefined}
        onKeyDown={onKeyDown}
        // Focus the stage itself, not the first button, so nothing wears a focus ring on open.
        onOpenAutoFocus={(e) => {
          e.preventDefault()
          ;(e.currentTarget as HTMLElement | null)?.focus()
        }}
      >
        <motion.div
          initial={reduce ? false : { opacity: 0, scale: 0.965, y: 12 }}
          animate={{ opacity: 1, scale: 1, y: 0 }}
          exit={reduce ? { opacity: 0 } : { opacity: 0, scale: 0.975, y: 8 }}
          transition={SPRING}
          className="fixed inset-0 z-modal flex flex-col outline-none"
          tabIndex={-1}
          onPointerMove={wake}
        >
          <DialogPrimitive.Title className="sr-only">{name}</DialogPrimitive.Title>

          {/* Floating glass control bar */}
          <motion.div
            animate={{ opacity: chrome ? 1 : 0, y: chrome ? 0 : -14 }}
            transition={reduce ? { duration: 0 } : SPRING}
            className={cn('pointer-events-none absolute inset-x-0 top-3 z-10 flex justify-center px-3', !chrome && '[&>*]:pointer-events-none')}
          >
            <div className="pointer-events-auto flex max-w-full items-center gap-1 rounded-2xl border border-white/10 bg-black/65 p-1.5 pl-2.5 text-white shadow-[0_18px_50px_-12px_color-mix(in_srgb,black_70%,transparent)] backdrop-blur-2xl">
              <span className="grid size-7 shrink-0 place-items-center rounded-lg bg-white/15"><FileText className="size-3.5" /></span>
              <div className="mx-1.5 min-w-0 max-w-[14rem] sm:max-w-[22rem]">
                <p className="truncate text-sm font-semibold leading-tight" title={name}>{name}</p>
                <p className="truncate text-tiny leading-tight text-white/55">
                  {load.state === 'ready' ? `${load.sizes.length} page${load.sizes.length === 1 ? '' : 's'}` : 'PDF'}{sizeLabel ? ` · ${sizeLabel}` : ''}
                </p>
              </div>
              {load.state === 'ready' ? (
                <>
                  <Sep />
                  <Glass label="Zoom out" onClick={() => applyZoom(zoomRef.current / 1.2)} disabled={zoom <= ZOOM_MIN}><Minus className="size-4" /></Glass>
                  <button type="button" onClick={() => applyZoom(1)} className="num h-8 min-w-[3.25rem] rounded-lg px-1.5 text-xs font-semibold text-white/85 transition-colors hover:bg-white/10" title="Fit to width">
                    {Math.round(zoom * 100)}%
                  </button>
                  <Glass label="Zoom in" onClick={() => applyZoom(zoomRef.current * 1.2)} disabled={zoom >= ZOOM_MAX}><Plus className="size-4" /></Glass>
                  <Glass label="Fit to width" onClick={() => applyZoom(1)} className="hidden sm:grid"><Maximize2 className="size-3.5" /></Glass>
                </>
              ) : null}
              <Sep />
              <Glass label="Open in new tab" onClick={() => void openTab()} className="hidden sm:grid"><ExternalLink className="size-3.5" /></Glass>
              <Glass label="Download" onClick={() => void download()} disabled={downloading}>
                {downloading ? <Loader2 className="size-3.5 animate-spin" /> : <Download className="size-3.5" />}
              </Glass>
              <DialogPrimitive.Close asChild>
                <button type="button" aria-label="Close" className="ml-0.5 grid size-8 place-items-center rounded-full bg-white text-black transition-transform active:scale-90">
                  <X className="size-4" strokeWidth={2.5} />
                </button>
              </DialogPrimitive.Close>
            </div>
          </motion.div>

          {/* Stage */}
          {load.state === 'native' ? (
            <div className="flex min-h-0 flex-1 justify-center px-2 pb-2 pt-[4.5rem] sm:px-6 sm:pb-6">
              <iframe src={load.url} title={name} className="size-full max-w-5xl rounded-xl border-0 bg-white shadow-2xl" />
            </div>
          ) : (
            <div
              ref={scrollRef}
              onScroll={onScroll}
              className="scrollbar-none min-h-0 flex-1 overflow-auto overscroll-contain"
              onClick={(e) => {
                if (e.target === e.currentTarget) setChrome((c) => !c)
              }}
            >
              {load.state === 'loading' ? (
                <div className="flex flex-col items-center gap-5 px-4 pt-24">
                  {[0, 1].map((i) => (
                    <motion.div
                      key={i}
                      initial={reduce ? false : { opacity: 0, y: 16 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ ...SPRING, delay: i * 0.06 }}
                      className="aspect-[8.5/11] w-full max-w-3xl overflow-hidden rounded-md bg-white/[0.07]"
                    >
                      <div className="size-full animate-pulse bg-gradient-to-br from-white/[0.06] via-white/[0.12] to-white/[0.04]" />
                    </motion.div>
                  ))}
                </div>
              ) : load.state === 'error' ? (
                <div className="grid h-full place-items-center px-6 text-center text-sm text-white/80">
                  <div>
                    <p className="font-semibold text-white">Couldn’t open this PDF.</p>
                    <p className="mt-1 text-white/60">Download it instead, from the bar above.</p>
                  </div>
                </div>
              ) : (
                <div className="mx-auto flex w-max min-w-full flex-col items-center px-4" style={{ paddingTop: 72, paddingBottom: 96, gap: PAGE_GAP }}>
                  {load.sizes.map((s, i) => (
                    <PageCanvas key={i} doc={load.doc} index={i + 1} width={s.w * scale} height={s.h * scale} scale={scale} root={scrollRef} reduce={reduce} />
                  ))}
                </div>
              )}
            </div>
          )}

          {/* Page counter */}
          {load.state === 'ready' && load.sizes.length > 1 ? (
            <motion.div
              animate={{ opacity: chrome ? 1 : 0, y: chrome ? 0 : 14 }}
              transition={reduce ? { duration: 0 } : SPRING}
              className="pointer-events-none absolute inset-x-0 bottom-5 flex justify-center"
            >
              <div className="pointer-events-auto flex items-center gap-0.5 rounded-full border border-white/10 bg-black/65 p-1 text-white shadow-[0_18px_50px_-12px_color-mix(in_srgb,black_70%,transparent)] backdrop-blur-2xl">
                <Glass label="Previous page" onClick={() => goTo(page - 1)} disabled={page <= 1} round><ChevronUp className="size-4" /></Glass>
                <span className="num min-w-[4.5rem] text-center text-xs font-semibold">
                  <AnimatePresence mode="popLayout" initial={false}>
                    <motion.span key={page} initial={reduce ? false : { opacity: 0, y: 6 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -6 }} transition={{ duration: 0.16 }} className="inline-block">
                      {page}
                    </motion.span>
                  </AnimatePresence>
                  <span className="text-white/55"> / {load.sizes.length}</span>
                </span>
                <Glass label="Next page" onClick={() => goTo(page + 1)} disabled={page >= load.sizes.length} round><ChevronDown className="size-4" /></Glass>
              </div>
            </motion.div>
          ) : null}
        </motion.div>
      </DialogPrimitive.Content>
    </DialogPrimitive.Portal>
  )
}

function Sep() {
  return <span className="mx-1 h-5 w-px shrink-0 bg-white/15" aria-hidden />
}

function Glass({ label, onClick, disabled, children, className, round }: { label: string; onClick: () => void; disabled?: boolean; children: React.ReactNode; className?: string; round?: boolean }) {
  return (
    <button
      type="button"
      aria-label={label}
      title={label}
      onClick={onClick}
      disabled={disabled}
      className={cn('grid size-8 shrink-0 place-items-center text-white/85 transition-[background-color,transform,opacity] hover:bg-white/12 hover:text-white active:scale-90 disabled:opacity-30 disabled:active:scale-100', round ? 'rounded-full' : 'rounded-lg', className)}
    >
      {children}
    </button>
  )
}

/**
 * One page: a white sheet sized to the current zoom, drawn when it nears the screen and redrawn
 * (at device resolution) once a zoom settles. Until then the previous bitmap stretches, so
 * zooming feels instant.
 */
function PageCanvas({ doc, index, width, height, scale, root, reduce }: { doc: PdfDocument; index: number; width: number; height: number; scale: number; root: React.RefObject<HTMLDivElement | null>; reduce: boolean | null }) {
  const holder = useRef<HTMLDivElement>(null)
  const canvas = useRef<HTMLCanvasElement>(null)
  const [near, setNear] = useState(index <= 2)
  const [drawnAt, setDrawnAt] = useState(0)

  useEffect(() => {
    const el = holder.current
    if (!el || typeof IntersectionObserver === 'undefined') return setNear(true)
    const io = new IntersectionObserver(([e]) => e?.isIntersecting && setNear(true), { root: root.current, rootMargin: '1200px 0px' })
    io.observe(el)
    return () => io.disconnect()
  }, [root])

  useEffect(() => {
    if (!near) return
    let cancelled = false
    let task: { cancel: () => void } | null = null
    const t = window.setTimeout(async () => {
      try {
        const page = await doc.getPage(index)
        if (cancelled || !canvas.current) return
        const dpr = Math.min(window.devicePixelRatio || 1, 3)
        const viewport = page.getViewport({ scale: scale * dpr })
        const c = canvas.current
        // Draw offscreen, then swap, so the visible bitmap never flashes blank mid-zoom.
        const off = document.createElement('canvas')
        off.width = Math.floor(viewport.width)
        off.height = Math.floor(viewport.height)
        const render = page.render({ canvasContext: off.getContext('2d')!, viewport })
        task = render
        await render.promise
        if (cancelled) return
        c.width = off.width
        c.height = off.height
        c.getContext('2d')!.drawImage(off, 0, 0)
        setDrawnAt(scale)
      } catch {
        // A cancelled render throws; the next settle draws it.
      }
    }, drawnAt ? 140 : 0)
    return () => {
      cancelled = true
      window.clearTimeout(t)
      task?.cancel()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- redraw on zoom settle
  }, [near, scale, doc, index])

  return (
    <div ref={holder} className="relative shrink-0 overflow-hidden rounded-[3px] bg-white shadow-[0_10px_40px_-8px_color-mix(in_srgb,black_55%,transparent)]" style={{ width, height }}>
      <motion.canvas
        ref={canvas}
        initial={false}
        animate={{ opacity: drawnAt ? 1 : 0 }}
        transition={reduce ? { duration: 0 } : { duration: 0.25 }}
        className="absolute inset-0 size-full"
      />
      {!drawnAt ? <div className="absolute inset-0 animate-pulse bg-gradient-to-b from-black/[0.02] to-black/[0.05]" /> : null}
    </div>
  )
}
